import { consoleRoutes, FACING_ONLY_TRIGGERS, isFacingTrigger, type WorkflowTrigger } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, Button, Icon, StatusBadge, toast } from "@sagot/ui";
import { useErrorMessage } from "../../../lib/api-errors";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { useFormatters } from "../shared/format";
import { kindOfWorkflow } from "./definition-model";
import { goalRate, shareOf } from "./goal-stats";
import { KindBadge } from "./kind-badge";
import type { WorkflowOut } from "./types";
import { useWorkflowMutations } from "./use-workflows";
import { STATE_STATUS } from "./workflow-header";

const W = "engage.workflows";

interface TriggerTableProps {
  trigger: WorkflowTrigger;
  rows: WorkflowOut[];
  canPublish: boolean;
}

/** The workspace's own workflows of one trigger, in the order they run, with their 30-day numbers. */
export function TriggerTable({ trigger, rows, canPublish }: TriggerTableProps) {
  const { t } = useTranslation();
  const format = useFormatters();
  const message = useErrorMessage();
  const { summary } = useWorkspaceContext();
  const { reorder } = useWorkflowMutations();
  const move = (index: number, to: number) => {
    const ids = rows.map((row) => row.id);
    const [moved] = ids.splice(index, 1);
    if (moved) ids.splice(to, 0, moved);
    reorder.mutate({ trigger, workflow_ids: ids }, { onSuccess: () => toast.success(t(`${W}.reordered`)), onError: (e) => { toast.error(message(e)); } });
  };
  // A workflow the plan no longer includes keeps its place but cannot be reordered (it can still be paused or deleted).
  const anyLocked = rows.some((row) => row.locked);
  return (
    <div>
      <h3 className="mb-1 text-sm font-bold">{t(`${W}.triggers.${trigger}`)} <span className="text-fg-subtle">({rows.length})</span></h3>
      {isFacingTrigger(trigger) ? (
        <p className="mb-2 flex items-center gap-2 rounded-md bg-info-soft px-3 py-2 text-xs text-fg-muted" data-testid="facing-note">
          <Icon name="info" className="size-3.5 shrink-0" />
          {t(FACING_ONLY_TRIGGERS.has(trigger) ? `${W}.facingNote` : `${W}.mixedNote`)}
        </p>
      ) : null}
      <div className="relative overflow-x-auto rounded-md border border-line">
        <table className="table">
          <thead>
            <tr>
              <th>{t(`${W}.columns.title`)}</th>
              <th>{t(`${W}.columns.kind`)}</th>
              <th>{t(`${W}.columns.state`)}</th>
              <th>{t(`${W}.columns.channels`)}</th>
              <th>{t(`${W}.columns.updated`)}</th>
              <th className="text-right">{t(`${W}.columns.sent`)}</th>
              <th className="text-right">{t(`${W}.columns.engaged`)}</th>
              <th className="text-right">{t(`${W}.columns.completed`)}</th>
              <th className="text-right">{t(`${W}.columns.goal`)}</th>
              <th className="text-right">{t(`${W}.columns.failed`)}</th>
              <th><span className="sr-only">{t(`${W}.columns.order`)}</span></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.id} data-testid="workflow-row">
                <td>
                  <AppLink className="font-semibold text-primary-ink hover:underline" href={consoleRoutes.workflow(summary.slug, row.id)}>{row.name}</AppLink>
                  {row.has_unpublished_changes && row.state !== "draft" ? <Badge tone="warning" className="ml-2">{t(`${W}.unpublished`)}</Badge> : null}
                  {row.locked ? <Badge className="ml-2" data-testid="locked-badge"><Icon name="lock" className="size-3" />{t(`${W}.defaults.upgrade.locked`)}</Badge> : null}
                </td>
                <td><KindBadge kind={kindOfWorkflow(row)} /></td>
                <td><StatusBadge status={STATE_STATUS[row.state] ?? "neutral"} label={t(`${W}.states.${row.state}`, { defaultValue: row.state })} /></td>
                <td className="text-xs text-fg-muted">{row.channels.length > 0 ? row.channels.map((c) => t(`engage.channels.${c}`)).join(", ") : t(`${W}.allChannels`)}</td>
                <td className="min-w-32 text-xs text-fg-muted">{t(`${W}.updatedBy`, { when: format.dateTime(row.updated_at), name: row.updated_by_name ?? "—" })}</td>
                <td className="text-right tabular-nums">{row.stats.sent}</td>
                <td className="text-right tabular-nums" data-testid="engaged-rate">{format.percent(shareOf(row.stats.engaged, row.stats.sent))}</td>
                <td className="text-right tabular-nums">{row.stats.completed}</td>
                <td className="text-right tabular-nums" data-testid="goal-rate" title={row.goal ? t(`${W}.goalTitle`, { event: t(`${W}.goal.events.${row.goal.event}`) }) : undefined}>{format.percent(goalRate(row.stats))}</td>
                <td className={row.stats.failed > 0 ? "text-right tabular-nums text-danger" : "text-right tabular-nums"}>{row.stats.failed}</td>
                <td className="whitespace-nowrap text-right">
                  <Button variant="ghost" size="sm" iconOnly disabled={!canPublish || anyLocked || index === 0} aria-label={t(`${W}.moveUp`, { name: row.name })} onClick={() => { move(index, index - 1); }}>
                    <Icon name="chevron-up" />
                  </Button>
                  <Button variant="ghost" size="sm" iconOnly disabled={!canPublish || anyLocked || index === rows.length - 1} aria-label={t(`${W}.moveDown`, { name: row.name })} onClick={() => { move(index, index + 1); }}>
                    <Icon name="chevron-down" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
